import { useMemo } from "react";
import {
  BarChart,
  Bar,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";

import { SYSTEM_COLORS } from "../../../../components/system.colors";
import { compactCurrencyFormatter, createDecimalFormatter } from "../../../../utils/formatter/formatter";

import { SectionTitle } from "./SectionTitle";

const decimalFmt = createDecimalFormatter({ maximumFractionDigits: 1 });

const AGE_HUE_START = 142;
const AGE_HUE_END = 4;

const ageColor = (index, total, { saturation, lightness }) => {
  const ratio = total > 1 ? index / (total - 1) : 0;
  const hue = AGE_HUE_START + (AGE_HUE_END - AGE_HUE_START) * ratio;
  return `hsl(${hue.toFixed(0)}, ${saturation}%, ${lightness}%)`;
};

const vendaColor = (index, total) => ageColor(index, total, { saturation: 55, lightness: 45 });
const estoqueColor = (index, total) => ageColor(index, total, { saturation: 55, lightness: 78 });

// Valores que arredondam para zero saem como "0.0%", sem o sinal.
const pct = (value, digits = 1) => `${(value * 100).toFixed(digits).replace(/^-(0\.?0*)$/, "$1")}%`;

const StockTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;

  // As barras usam vendaMil/estoqueMil; o bucket original (em R$) vem em payload[0].payload.
  const { venda = 0, estoque = 0 } = payload[0].payload ?? {};

  return (
    <div style={{
      background: SYSTEM_COLORS.WHITE.WHITE,
      border: `1px solid ${SYSTEM_COLORS.GRAY.SILVER}`,
      borderRadius: "10px",
      padding: "0.7rem 0.9rem",
      boxShadow: "0 4px 16px rgba(0,0,0,0.1)",
      fontSize: "0.78rem",
    }}>
      <div style={{ fontWeight: 700, color: SYSTEM_COLORS.BLACK.EERIE_BLACK, marginBottom: "0.4rem" }}>{label}</div>
      <div style={{ color: SYSTEM_COLORS.GRAY.MEDIUM_GRAY }}>
        {`Venda líquida : ${compactCurrencyFormatter(venda)}`}
      </div>
      <div style={{ color: SYSTEM_COLORS.GRAY.MEDIUM_GRAY }}>
        {`Estoque PMV : ${compactCurrencyFormatter(estoque)}`}
      </div>
    </div>
  );
};

const bucketTdStyle = {
  padding: "0.7rem 0.8rem",
  fontSize: "0.85rem",
  color: SYSTEM_COLORS.BLACK.EERIE_BLACK,
  textAlign: "right",
  borderBottom: `1px solid ${SYSTEM_COLORS.GRAY.PLATINUM}`,
};

const bucketThStyle = {
  padding: "0.6rem 0.8rem",
  fontSize: "0.68rem",
  fontWeight: 700,
  color: SYSTEM_COLORS.GRAY.GREY,
  letterSpacing: "0.3px",
  textAlign: "right",
  borderBottom: `1px solid ${SYSTEM_COLORS.GRAY.PLATINUM}`,
};

export function StockHealthSection({ ctx }) {
  const { buckets = [], total } = ctx || {};

  const chartData = useMemo(
    () => buckets.map((bucket) => ({ ...bucket, vendaMil: bucket.venda / 1000, estoqueMil: bucket.estoque / 1000 })),
    [buckets]
  );

  const yAxisMax = useMemo(() => {
    const maxValue = Math.max(0, ...chartData.flatMap((item) => [item.vendaMil, item.estoqueMil]));
    return Math.max(50, Math.ceil((maxValue * 1.15) / 50) * 50);
  }, [chartData]);

  const shareUnderTwelveWeeks = useMemo(() => {
    if (!total?.estoque) return 0;
    const upToTwelveWeeks = buckets
      .filter((bucket) => !bucket.openEnded && bucket.maxWeeks <= 12)
      .reduce((sum, bucket) => sum + bucket.estoque, 0);
    return upToTwelveWeeks / total.estoque;
  }, [buckets, total]);

  return (
    <section style={{ marginBlockStart: "2rem" }}>
      <SectionTitle
        title="SAÚDE DO ESTOQUE"
        description="Estoque dividido em buckets de idade. Os baldes mais novos aparecem em verde e os mais antigos em amarelo e vermelho, mostrando quanto cada faixa representa da venda e do estoque."
      />

      <div style={{ display: "grid", gridTemplateColumns: "1.2fr 1fr", gap: "1.5rem", alignItems: "stretch" }}>
        <div style={{
          background: SYSTEM_COLORS.WHITE.WHITE,
          border: `1px solid ${SYSTEM_COLORS.GRAY.LIGHT_GRAY}`,
          borderRadius: "14px",
          boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
          padding: "1.5rem 2rem",
          display: "flex",
          flexDirection: "column",
        }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBlockEnd: "1rem" }}>
            <span style={{ fontSize: "0.75rem", fontWeight: 700, color: SYSTEM_COLORS.GRAY.CHARCOAL, letterSpacing: "0.4px", textTransform: "uppercase" }}>
              Venda x Estoque por Idade
            </span>
            <span style={{ fontSize: "0.8rem", fontWeight: 700, color: SYSTEM_COLORS.BLACK.EERIE_BLACK }}>
              {`${pct(shareUnderTwelveWeeks)} do estoque com até 12 semanas`}
            </span>
          </div>

          <div style={{ flex: 1, minBlockSize: "19rem" }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData} margin={{ top: 24, right: 16, left: 0, bottom: 0 }} barGap={4}>
                <CartesianGrid vertical={false} stroke={SYSTEM_COLORS.GRAY.PLATINUM} />
                <XAxis dataKey="label" axisLine={false} tickLine={false} tick={{ fontSize: 11, fill: SYSTEM_COLORS.GRAY.TAUPE_GRAY }} />
                <YAxis
                  domain={[0, yAxisMax]}
                  axisLine={false}
                  tickLine={false}
                  tick={{ fontSize: 11, fill: SYSTEM_COLORS.GRAY.TAUPE_GRAY }}
                  tickFormatter={(v) => (v === 0 ? "R$ 0" : `R$ ${decimalFmt.format(v)} mil`)}
                />
                <Tooltip content={<StockTooltip />} cursor={{ fill: "rgba(0,0,0,0.04)" }} />
                <Bar dataKey="vendaMil" name="Venda líquida" radius={[3, 3, 0, 0]} maxBarSize={30}>
                  {chartData.map((bucket, index) => (
                    <Cell key={bucket.key} fill={vendaColor(index, chartData.length)} />
                  ))}
                </Bar>
                <Bar dataKey="estoqueMil" name="Estoque PMV" radius={[3, 3, 0, 0]} maxBarSize={30}>
                  {chartData.map((bucket, index) => (
                    <Cell key={bucket.key} fill={estoqueColor(index, chartData.length)} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>

          <div style={{ display: "flex", justifyContent: "center", gap: "1.2rem", marginBlockStart: "0.5rem" }}>
            <span style={{ display: "inline-flex", alignItems: "center", gap: "0.4rem", fontSize: "0.75rem", color: SYSTEM_COLORS.GRAY.MEDIUM_GRAY }}>
              <span style={{ inlineSize: "10px", blockSize: "10px", borderRadius: "2px", background: SYSTEM_COLORS.GREEN.FOREST_GREEN, display: "inline-block" }} />
              Venda líquida
            </span>
            <span style={{ display: "inline-flex", alignItems: "center", gap: "0.4rem", fontSize: "0.75rem", color: SYSTEM_COLORS.GRAY.MEDIUM_GRAY }}>
              <span style={{ inlineSize: "10px", blockSize: "10px", borderRadius: "2px", background: `hsl(142, 55%, 78%)`, display: "inline-block" }} />
              Estoque PMV
            </span>
          </div>
        </div>

        <div style={{
          background: SYSTEM_COLORS.WHITE.WHITE,
          border: `1px solid ${SYSTEM_COLORS.GRAY.LIGHT_GRAY}`,
          borderRadius: "14px",
          boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
          padding: "1.5rem 2rem",
        }}>
          <div style={{ fontSize: "0.75rem", fontWeight: 700, color: SYSTEM_COLORS.GRAY.CHARCOAL, letterSpacing: "0.4px", textTransform: "uppercase", marginBlockEnd: "1rem" }}>
            Buckets de Idade
          </div>

          <table style={{ inlineSize: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <th style={{ ...bucketThStyle, textAlign: "left" }}>Bucket</th>
                <th style={bucketThStyle}>Venda</th>
                <th style={bucketThStyle}>Estoque</th>
                <th style={bucketThStyle}>Part. VD</th>
                <th style={bucketThStyle}>Part. Est</th>
              </tr>
            </thead>
            <tbody>
              {buckets.map((bucket, index) => (
                <tr key={bucket.key}>
                  <td style={{ ...bucketTdStyle, textAlign: "left", fontWeight: 700 }}>
                    <span style={{ display: "inline-flex", alignItems: "center", gap: "0.5rem" }}>
                      <span style={{ inlineSize: "9px", blockSize: "9px", borderRadius: "50%", background: vendaColor(index, buckets.length), flexShrink: 0 }} />
                      {bucket.label}
                      <span style={{ fontWeight: 400, color: SYSTEM_COLORS.GRAY.GREY, fontSize: "0.72rem" }}>
                        {`(${bucket.diasLabel})`}
                      </span>
                    </span>
                  </td>
                  <td style={bucketTdStyle}>{compactCurrencyFormatter(bucket.venda)}</td>
                  <td style={bucketTdStyle}>{compactCurrencyFormatter(bucket.estoque)}</td>
                  <td style={bucketTdStyle}>{pct(bucket.partVenda)}</td>
                  <td style={bucketTdStyle}>{pct(bucket.partEstoque)}</td>
                </tr>
              ))}
            </tbody>
            {total && (
              <tfoot>
                <tr style={{ borderTop: `2px solid ${SYSTEM_COLORS.GRAY.PLATINUM}` }}>
                  <td style={{ ...bucketTdStyle, textAlign: "left", fontWeight: 700 }}>Total geral</td>
                  <td style={{ ...bucketTdStyle, fontWeight: 700 }}>{compactCurrencyFormatter(total.venda)}</td>
                  <td style={{ ...bucketTdStyle, fontWeight: 700 }}>{compactCurrencyFormatter(total.estoque)}</td>
                  <td style={{ ...bucketTdStyle, fontWeight: 700 }}>{pct(total.partVenda, 0)}</td>
                  <td style={{ ...bucketTdStyle, fontWeight: 700 }}>{pct(total.partEstoque, 0)}</td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>
    </section>
  );
}
